import type { EnumUnion } from '@dresscode-frontend/utility-types';
import type { DevDocumentKind, DevDocumentListItem, DevDocumentStatus } from '@/features/stacktrace/dev-documents/-query/type';

/**
 * 開発ドキュメント台帳 一覧のテーブル列 ID。
 *
 * 表示順:
 *   コード / タイトル / 種別 / ステータス / プロダクト / リンク / 更新日
 *
 * 出自（OWNERSHIP）と同期状態（SYNC_STATE）は画面から外した。API のフィールドは
 * 残っているので、自動生成を復活させるときに列を戻せる。
 */
export const DevDocumentTableColumns = {
  CODE: 'CODE',
  TITLE: 'TITLE',
  KIND: 'KIND',
  STATUS: 'STATUS',
  PRODUCT: 'PRODUCT',
  SOURCE: 'SOURCE',
  UPDATED_AT: 'UPDATED_AT'
} as const;
export type DevDocumentTableColumns = EnumUnion<typeof DevDocumentTableColumns>;
type DevDocumentCodeColumn = {
  id: string;
  code: string;
};
type DevDocumentProductColumn = {
  id: string;
  name: string;
};

/**
 * リンク列の値。
 * 画面から登録したドキュメントは参考リンク（`externalUrl`）、棚卸しが取り込んだ
 * ミラーは源泉ファイルのパス（`sourcePath`）が入る。どちらも無ければ null。
 */
type DevDocumentSourceColumn = {
  text: string | null;
};
export type DevDocumentTableRowData = {
  [DevDocumentTableColumns.CODE]: DevDocumentCodeColumn;
  [DevDocumentTableColumns.TITLE]: string;
  [DevDocumentTableColumns.KIND]: DevDocumentKind | undefined;
  [DevDocumentTableColumns.STATUS]: DevDocumentStatus | undefined;
  [DevDocumentTableColumns.PRODUCT]: DevDocumentProductColumn;
  [DevDocumentTableColumns.SOURCE]: DevDocumentSourceColumn;
  [DevDocumentTableColumns.UPDATED_AT]: string | null;
};
export const convertDevDocumentItemToTableRow = (item: DevDocumentListItem): DevDocumentTableRowData => {
  return {
    [DevDocumentTableColumns.CODE]: {
      id: item.id,
      code: item.code
    },
    [DevDocumentTableColumns.TITLE]: item.title,
    [DevDocumentTableColumns.KIND]: item.kind as DevDocumentKind | undefined,
    [DevDocumentTableColumns.STATUS]: item.status as DevDocumentStatus | undefined,
    [DevDocumentTableColumns.PRODUCT]: {
      id: item.productId,
      name: item.productName
    },
    [DevDocumentTableColumns.SOURCE]: {
      text: item.externalUrl ?? item.sourcePath ?? null
    },
    // 台帳側の更新日時。`sourceUpdatedAt`（源泉ファイルの更新日）は棚卸しで
    // 取り込んだ行にしか入らず、画面から作ったドキュメントでは常に空になる。
    [DevDocumentTableColumns.UPDATED_AT]: item.updatedAt
  };
};